import { useState, RefObject, useCallback } from 'react'
import { IGridItem, IPositionParams, IPoint, IActiveElement } from '../../../grid.types'
import useMouseDragHandler from './useMouseDragHandler'
import calculatePositionUtils from '../calculatePosition.utils'

const useResizeHandlers = (
  containerRef: RefObject<HTMLDivElement>,
  handleRef: RefObject<HTMLDivElement>,
  gridItem: IGridItem,
  posParams: IPositionParams,
  onActiveElementChange: (activeElement: IActiveElement) => void,
  onResize: (width: number, height: number, key: IGridItem['key']) => void
) => {
  const [cornerPos, setCornerPos] = useState<IPoint | null>(null)
  const [isResizing, setIsResizing] = useState(false)

  const onStart = useCallback(() => {
    if (!containerRef.current) return
    setIsResizing(true)
    onActiveElementChange({ key: gridItem.key, movedType: 'resizing' })
    containerRef.current.style.transition = 'none'
    containerRef.current.style.zIndex = '3'
    containerRef.current.style.userSelect = 'none'
    document.body.style.cursor = 'nwse-resize'
  }, [containerRef, gridItem, onActiveElementChange])

  const onMove = useCallback(
    (delta: IPoint) => {
      if (!containerRef.current) return
      const colWidth = calculatePositionUtils.calcGridColWidth(posParams)
      const dx = gridItem.width * colWidth + (gridItem.width - 1) * posParams.margin[0] + delta.x
      const dy = gridItem.height * posParams.rowHeight + (gridItem.height - 1) * posParams.margin[1] + delta.y

      const { maxHeight, minHeight, maxWidth, minWidth } = calculatePositionUtils.calcWHBoundsToPx(
        colWidth,
        posParams.rowHeight,
        posParams.margin,
        gridItem.bounds ?? {}
      )

      const clampedX = calculatePositionUtils.clamp(dx, minWidth, maxWidth ?? dx)
      const clampedY = calculatePositionUtils.clamp(dy, minHeight, maxHeight ?? dy)
      setCornerPos({ x: clampedX, y: clampedY })

      const { w, h } = calculatePositionUtils.calcWH(
        posParams,
        dx,
        dy,
        gridItem.x,
        gridItem.y,
        calculatePositionUtils.getHandle(delta.x, delta.y),
        gridItem.bounds ?? {}
      )

      onResize(w, h, gridItem.key)
    },
    [posParams, gridItem, onResize]
  )

  const onEnd = useCallback(() => {
    if (!containerRef.current) return
    setIsResizing(false)
    setCornerPos(null)
    onActiveElementChange(null)
    containerRef.current.style.transition = 'all 0.2s ease'
    containerRef.current.style.zIndex = '2'
    containerRef.current.style.userSelect = 'text'
    document.body.style.cursor = ''
  }, [containerRef, onActiveElementChange])

  useMouseDragHandler({
    ref: handleRef,
    enabled: posParams.containerWidth != null,
    onStart,
    onMove,
    onEnd,
  })

  return { isResizing, cornerPos }
}

export default useResizeHandlers
